import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as AscIcon,
  ArrowUpward as DescIcon,
  Block as BlockIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Close as CloseIcon,
  EditOutlined as EditIcon,
  LockOpen as UnblockIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Send as SendIcon,
  SwapVert as SortIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { tenantApi, type SearchField } from '@/api/tenant';
import {
  sendRepRequest, cancelRepRequest, getMyRepRequests,
  getIncomingRepRequests, acceptRepRequest, rejectRepRequest,
  directToggleRep, blockRep as blockRepApi, unblockRep as unblockRepApi,
  updateRetailerScope,
  type RepRequestDto,
} from '@/api/repRequests';
import { useAuthStore } from '@/stores/authStore';
import type { PeerTenant } from '@/types';
import { PartyDetailDialog } from '@/components/PartyDetailDialog';
import { brand } from '@/theme/theme';
import { SearchWithFieldSelect } from '@/components/SearchWithFieldSelect';
import { PaginationFooter } from '@/components/PaginationFooter';

// ─── LocalStorage helpers (retailers & blocked only — requests use backend) ──

const REP_ASSIGNMENTS_KEY = 'rep_retailer_assignments';
const BLOCKED_REPS_KEY = 'blocked_reps';
const REP_MY_RETAILERS_KEY = 'rep_my_retailers';

interface RepAssignment {
  supplierTenantId: number;
  repTenantId: number;
  mode: 'ALL' | 'LIMITED';
  retailerIds: number[];
}

interface BlockedEntry {
  supplierTenantId: number;
  repTenantId: number;
}

function loadAssignments(): RepAssignment[] {
  try { return JSON.parse(localStorage.getItem(REP_ASSIGNMENTS_KEY) ?? '[]') as RepAssignment[]; } catch { return []; }
}
function saveAssignments(a: RepAssignment[]) { localStorage.setItem(REP_ASSIGNMENTS_KEY, JSON.stringify(a)); }

function loadBlocked(): BlockedEntry[] {
  try { return JSON.parse(localStorage.getItem(BLOCKED_REPS_KEY) ?? '[]') as BlockedEntry[]; } catch { return []; }
}
function saveBlocked(b: BlockedEntry[]) { localStorage.setItem(BLOCKED_REPS_KEY, JSON.stringify(b)); }

interface RepMyRetailers { repTenantId: number; retailerIds: number[]; }
function loadMyRetailers(): RepMyRetailers[] {
  try { return JSON.parse(localStorage.getItem(REP_MY_RETAILERS_KEY) ?? '[]') as RepMyRetailers[]; } catch { return []; }
}
function saveMyRetailers(r: RepMyRetailers[]) { localStorage.setItem(REP_MY_RETAILERS_KEY, JSON.stringify(r)); }

// ─── Debounce hook ────────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ─── Shared cell styles ────────────────────────────────────────────────────
const thSx = {
  fontWeight: 700, fontSize: 11, letterSpacing: '0.06em',
  color: brand.inkSoft, bgcolor: '#f8f9fc', textTransform: 'uppercase' as const,
  whiteSpace: 'nowrap' as const,
};

// ─── Tenant type badge — circular letter, same convention as Order Inbox's
// buyer-type badge: R for retailer, W for wholesaler, SR for sales rep. ────
const TYPE_PALETTE: Record<string, { bg: string; fg: string; letter: string }> = {
  RETAILER:       { bg: '#C17BAF', fg: '#FFFFFF', letter: 'R' },
  WHOLESALER:     { bg: '#7F77DD', fg: '#26215C', letter: 'W' },
  REPRESENTATIVE: { bg: '#FF6F61', fg: '#FFFFFF', letter: 'SR' },
};
function TenantTypeChip({ type }: { type?: string }) {
  if (!type) return null;
  const c = TYPE_PALETTE[type] ?? { bg: '#9CA3AF', fg: '#1F2937', letter: '?' };
  return (
    <Box sx={{
      width: c.letter.length > 1 ? 24 : 20, height: 20,
      borderRadius: c.letter.length > 1 ? '10px' : '50%',
      flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: c.bg, color: c.fg, fontSize: c.letter.length > 1 ? 9 : 10, fontWeight: 700,
    }}>
      {c.letter}
    </Box>
  );
}

// ─── Reusable peer table ───────────────────────────────────────────────────

type SortField = 'name' | 'location';

function SortHeader({ label, field, sortField, sortDir, onSort }: {
  label: string; field: SortField | null;
  sortField: SortField | null; sortDir: 'asc' | 'desc';
  onSort: (f: SortField) => void;
}) {
  const active = field && sortField === field;
  return (
    <TableCell
      sx={{ ...thSx, cursor: field ? 'pointer' : 'default', userSelect: 'none',
        '&:hover': field ? { bgcolor: '#eef0f8' } : {} }}
      onClick={() => field && onSort(field)}
    >
      <Stack direction="row" alignItems="center" spacing={0.4}>
        <span>{label}</span>
        {field && (active
          ? (sortDir === 'asc' ? <AscIcon sx={{ fontSize: 13, color: brand.violet }} /> : <DescIcon sx={{ fontSize: 13, color: brand.violet }} />)
          : <SortIcon sx={{ fontSize: 13, color: brand.inkSoft, opacity: 0.5 }} />)}
      </Stack>
    </TableCell>
  );
}

function PeerTable({
  rows, emptyMsg, renderAction, search, onSearch, searchField, page, rowsPerPage,
  onPageChange, onRowsPerPageChange, loading, rowSx, filterSlot, totalCount,
  hideAction, hideSearchBox, label = 'records',
}: {
  rows: PeerTenant[];
  emptyMsg: string;
  renderAction: (p: PeerTenant) => React.ReactNode;
  search: string;
  onSearch: (v: string) => void;
  /** When set, scopes client-mode filtering to a single field instead of the
   *  default OR-across-name/city/gstin/dl/phone. Server-mode tables ignore this
   *  (filtering already happened server-side via the API's own searchField param). */
  searchField?: SearchField;
  page: number;
  rowsPerPage: number;
  onPageChange: (p: number) => void;
  onRowsPerPageChange: (r: number) => void;
  loading?: boolean;
  rowSx?: (p: PeerTenant) => object;
  filterSlot?: React.ReactNode;
  /** When set the table is in server mode: rows are already the current page,
   *  no client-side filtering/sorting/slicing is applied, and this value is
   *  used as the pagination total. */
  totalCount?: number;
  /** Hide the entire Action column (header + cells). */
  hideAction?: boolean;
  /** Hide the built-in search box — used when the caller renders its own
   *  SearchWithFieldSelect elsewhere (e.g. in the page header) instead. */
  hideSearchBox?: boolean;
  /** Plural noun shown in the pagination footer's range label, e.g. "suppliers". */
  label?: string;
}) {
  const serverMode = totalCount !== undefined;

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [viewParty, setViewParty] = useState<PeerTenant | null>(null);

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    onPageChange(0);
  }

  // Client mode: filter + sort + paginate locally.
  // Server mode: rows are already the page, no transformation needed.
  const processed = useMemo(() => {
    if (serverMode) return rows;
    const q = search.trim().toLowerCase();
    let result = !q ? [...rows] : rows.filter(p => {
      if (searchField === 'name') return p.name.toLowerCase().includes(q);
      if (searchField === 'gst') return (p.gstin ?? '').toLowerCase().includes(q);
      if (searchField === 'phone') return (p.phone ?? '').includes(q);
      if (searchField === 'location') return (p.city ?? '').toLowerCase().includes(q);
      return (
        p.name.toLowerCase().includes(q) ||
        (p.city ?? '').toLowerCase().includes(q) ||
        (p.gstin ?? '').toLowerCase().includes(q) ||
        (p.drugLicenseNumber ?? '').toLowerCase().includes(q) ||
        (p.phone ?? '').includes(q));
    });

    if (sortField === 'name') {
      result.sort((a, b) => {
        const cmp = a.name.localeCompare(b.name);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    } else if (sortField === 'location') {
      result.sort((a, b) => {
        const la = [a.city, a.state].filter(Boolean).join('');
        const lb = [b.city, b.state].filter(Boolean).join('');
        const cmp = la.localeCompare(lb);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [rows, search, sortField, sortDir, serverMode]);

  const paginated = serverMode
    ? processed  // already a page from the server
    : processed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const paginationCount = serverMode ? totalCount! : processed.length;

  return (
    <>
      {(!hideSearchBox || filterSlot) && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap">
          {!hideSearchBox && (
            <TextField
              size="small"
              placeholder="Search by name, city, GST or phone…"
              value={search}
              onChange={e => { onSearch(e.target.value); if (!serverMode) onPageChange(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          )}
          {filterSlot && <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>{filterSlot}</Box>}
        </Stack>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader sx={{ minWidth: 680 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 48 }}>#</TableCell>
                  {serverMode
                    ? <TableCell sx={thSx}>Name</TableCell>
                    : <SortHeader label="Name" field="name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                  {serverMode
                    ? <TableCell sx={thSx}>Location</TableCell>
                    : <SortHeader label="Location" field="location" sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                  <TableCell sx={thSx}>GSTIN / DL</TableCell>
                  <TableCell sx={thSx}>Phone / Email</TableCell>
                  {!hideAction && <TableCell sx={thSx}>Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={hideAction ? 5 : 6} sx={{ textAlign: 'center', py: 6, color: brand.inkSoft }}>
                      {emptyMsg}
                    </TableCell>
                  </TableRow>
                ) : paginated.map((p, idx) => (
                  <TableRow key={p.id} hover sx={rowSx ? rowSx(p) : undefined}>
                    <TableCell sx={{ fontSize: 12, color: brand.inkSoft, width: 48 }}>
                      {page * rowsPerPage + idx + 1}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: p.address ? 0.25 : 0 }}>
                        <Typography
                          onClick={() => setViewParty(p)}
                          sx={{ fontSize: 13, fontWeight: 600, color: brand.navy, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {p.name}
                        </Typography>
                        <TenantTypeChip type={p.tenantType} />
                      </Stack>
                      {p.address && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.address}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>
                        {[p.city, p.district, p.state].filter(Boolean).join(', ') ? `📍 ${[p.city, p.district, p.state].filter(Boolean).join(', ')}` : '—'}
                      </Typography>
                      {p.pincode && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.pincode}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.2}>
                        {p.gstin && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>GST: {p.gstin}</Typography>}
                        {p.drugLicenseNumber && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>DL: {p.drugLicenseNumber}</Typography>}
                        {!p.gstin && !p.drugLicenseNumber && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>—</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        {p.phone && <Typography sx={{ fontSize: 12, color: brand.navy }}>📞 {p.phone}</Typography>}
                        {p.email && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.email}</Typography>}
                        {!p.phone && !p.email && <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>—</Typography>}
                      </Stack>
                    </TableCell>
                    {!hideAction && <TableCell>{renderAction(p)}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <PaginationFooter
            page={page}
            totalPages={Math.max(1, Math.ceil(paginationCount / rowsPerPage))}
            total={paginationCount}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            label={label}
          />
        </>
      )}

      <PartyDetailDialog party={viewParty} onClose={() => setViewParty(null)} />
    </>
  );
}

// ─── Shared rep data hook (REPRESENTATIVE side) ────────────────────────────
// Loads only the small "My" lists (via cache) + rep requests.
// "All Suppliers" / "All Retailers" are fetched on demand by each view via
// server-side search — we no longer preload 500 records here.

function useRepData(myTenantId: number) {
  /** Small cache for enriching "My Suppliers" display (name, phone, etc.) */
  const [suppliersCache, setSuppliersCache] = useState<PeerTenant[]>([]);
  /** Small cache for enriching "My Retailers" display */
  const [retailersCache, setRetailersCache] = useState<PeerTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [repRequests, setRepRequests] = useState<RepRequestDto[]>([]);
  const [myRetailerData, setMyRetailerData] = useState<RepMyRetailers[]>(loadMyRetailers);
  const [snack, setSnack] = useState('');

  async function fetchAll() {
    setLoading(true);
    const [suppRes, retRes, reqRes] = await Promise.allSettled([
      tenantApi.listPeers(undefined, 0, 200, 'WHOLESALER'),
      tenantApi.listPeers(undefined, 0, 200, 'RETAILER'),
      getMyRepRequests(),
    ]);
    if (suppRes.status === 'fulfilled') setSuppliersCache(suppRes.value.content);
    if (retRes.status === 'fulfilled') setRetailersCache(retRes.value.content);
    if (reqRes.status === 'fulfilled') setRepRequests(reqRes.value);
    setLoading(false);
  }

  useEffect(() => { void fetchAll(); }, []);

  // Poll rep requests every 30 s to pick up blocked/unblocked status changes
  // from the supplier's browser without requiring a manual refresh.
  useEffect(() => {
    const id = setInterval(async () => {
      const reqs = await getMyRepRequests().catch(() => null);
      if (reqs) setRepRequests(reqs);
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  const getReq = (supplierId: number) =>
    repRequests.find(r => r.supplierTenantId === supplierId);

  async function sendRequest(supplier: PeerTenant) {
    try {
      const created = await sendRepRequest(supplier.id);
      setRepRequests(prev => [
        ...prev.filter(r => r.supplierTenantId !== supplier.id),
        created,
      ]);
      setSnack(`Order request sent to ${supplier.name}`);
    } catch {
      setSnack('Failed to send request. Please try again.');
    }
  }

  async function cancelRequest(supplierId: number) {
    const req = getReq(supplierId);
    if (!req) return;
    try {
      await cancelRepRequest(req.id);
      setRepRequests(prev => prev.filter(r => r.supplierTenantId !== supplierId));
      setSnack('Request cancelled');
    } catch {
      setSnack('Failed to cancel request.');
    }
  }

  // mySuppliers: APPROVED requests, enriched from cache (fallback to req name)
  const mySuppliers = useMemo(() => {
    return repRequests
      .filter(r => r.status === 'APPROVED')
      .map(req => {
        const found = suppliersCache.find(s => s.id === req.supplierTenantId);
        return found ?? ({
          id: req.supplierTenantId,
          name: req.supplierName ?? `Supplier #${req.supplierTenantId}`,
          tenantType: 'WHOLESALER',
          city: '', district: '', state: '', phone: '', email: '', address: '', gstin: '', pincode: '',
        } as unknown as PeerTenant);
      });
  }, [suppliersCache, repRequests]);

  // Retailer IDs automatically assigned by accepted suppliers
  //  • ALL mode  → include every retailer the rep can see (from cache)
  //  • LIMITED   → include only the specific IDs the wholesaler picked
  const autoRetailerIds = useMemo(() => {
    const ids = new Set<number>();
    repRequests
      .filter(r => r.status === 'APPROVED')
      .forEach(r => {
        if (!r.retailerMode || r.retailerMode === 'ALL') {
          retailersCache.forEach(rc => ids.add(rc.id));
        } else {
          (r.retailerIds ?? []).forEach(id => ids.add(id));
        }
      });
    return ids;
  }, [repRequests, retailersCache]);

  // Effective set = manual (localStorage) + auto-assigned (from wholesaler scope)
  const myRetailerIds = useMemo(() => {
    const manualIds = myRetailerData.find(r => r.repTenantId === myTenantId)?.retailerIds ?? [];
    return [...new Set([...manualIds, ...autoRetailerIds])];
  }, [myRetailerData, myTenantId, autoRetailerIds]);

  function toggleRetailer(retailer: PeerTenant) {
    const isAdded = myRetailerIds.includes(retailer.id);
    const newIds = isAdded ? myRetailerIds.filter(id => id !== retailer.id) : [...myRetailerIds, retailer.id];
    const updated = [
      ...myRetailerData.filter(r => r.repTenantId !== myTenantId),
      { repTenantId: myTenantId, retailerIds: newIds },
    ];
    setMyRetailerData(updated); saveMyRetailers(updated);
    setSnack(isAdded ? `${retailer.name} removed from My Retailers` : `${retailer.name} added to My Retailers`);
  }

  // myRetailers: localStorage IDs enriched from cache
  const myRetailers = useMemo(
    () => retailersCache.filter(r => myRetailerIds.includes(r.id)),
    [retailersCache, myRetailerIds],
  );

  return {
    loading, fetchAll, repRequests,
    mySuppliers, myRetailers, myRetailerIds, autoRetailerIds,
    getReq, sendRequest, cancelRequest, toggleRetailer,
    snack, setSnack,
  };
}

// ─── RepView — Suppliers only (/salesreps) ────────────────────────────────

function RepView({ myTenantId }: { myTenantId: number; myName: string }) {
  const { loading, fetchAll, getReq, sendRequest, cancelRequest, snack, setSnack } = useRepData(myTenantId);
  const [tab, setTab] = useState(0); // 0=New Suppliers, 1=Near Me
  const [statusFilter, setStatusFilter] = useState<'all' | 'not_sent' | 'pending' | 'accepted' | 'rejected'>('all');

  // ── Search — shared across All / New / Near Me tabs, explicit search-icon trigger ──
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState<SearchField>('name');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedSearchField, setAppliedSearchField] = useState<SearchField>('name');
  const handleSearch = () => {
    setAppliedSearch(search.trim());
    setAppliedSearchField(searchField);
  };

  // ── All Suppliers tab (server search + pagination + status filter) ───────
  const [allPage, setAllPage] = useState(0);
  const [allRowsPerPage, setAllRowsPerPage] = useState(25);
  const [allRows, setAllRows] = useState<PeerTenant[]>([]);
  const [allTotal, setAllTotal] = useState(0);
  const [allLoading, setAllLoading] = useState(true);

  // ── New Suppliers / Near Me tabs (server search + filter, no status filter) ──
  const [userCity, setUserCity] = useState('');
  const [userArea, setUserArea] = useState('');
  const [nearRows, setNearRows] = useState<PeerTenant[]>([]);
  const [nearLoading, setNearLoading] = useState(false);

  useEffect(() => {
    tenantApi.getMe().then(me => {
      setUserCity(me.city ?? '');
      setUserArea(me.area ?? '');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== 0) return;
    let cancelled = false;
    setAllLoading(true);
    tenantApi.listPeers(appliedSearch || undefined, allPage, allRowsPerPage, 'WHOLESALER', {
      requestStatus: statusFilter !== 'all' ? statusFilter : undefined,
      searchField: appliedSearchField,
    })
      .then(res => { if (!cancelled) { setAllRows(res.content); setAllTotal(res.totalElements); setAllLoading(false); } })
      .catch(() => { if (!cancelled) setAllLoading(false); });
    return () => { cancelled = true; };
  }, [tab, appliedSearch, appliedSearchField, allPage, allRowsPerPage, statusFilter]);

  // New Suppliers (isNew) / Near Me (near) — both filtered server-side, not just
  // re-filtered against an already-fetched page.
  useEffect(() => {
    if (tab !== 1 && tab !== 2) return;
    let cancelled = false;
    setNearLoading(true);
    tenantApi.listPeers(appliedSearch || undefined, 0, 500, 'WHOLESALER', {
      isNew: tab === 1, near: tab === 2, searchField: appliedSearchField,
    }).then(res => {
      if (cancelled) return;
      setNearRows(res.content);
      setNearLoading(false);
    }).catch(() => { if (!cancelled) setNearLoading(false); });
    return () => { cancelled = true; };
  }, [tab, appliedSearch, appliedSearchField]);

  // Re-fetch when the user navigates back to this page (e.g. by clicking a REP_BLOCKED notification)
  const location = useLocation();
  const prevKeyRef = useRef(location.key);
  useEffect(() => {
    if (location.key !== prevKeyRef.current) {
      prevKeyRef.current = location.key;
      void fetchAll();
      // Also re-trigger the server search
      setAllPage(0);
    }
  }, [location.key, fetchAll]);

  function supplierAction(s: PeerTenant) {
    const req = getReq(s.id);
    if (!req || req.status === 'REJECTED' || req.status === 'CANCELLED') {
      return (
        <Button size="small" variant="contained" startIcon={<SendIcon />}
          onClick={() => sendRequest(s)}
          sx={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', bgcolor: brand.navy, '&:hover': { bgcolor: '#1e3460' } }}>
          {req?.status === 'REJECTED' ? 'Resend Request' : 'Send Request for Orders'}
        </Button>
      );
    }
    if (req.status === 'PENDING') return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Chip label="Request Pending" size="small" color="warning" sx={{ fontSize: 11 }} />
        <Button size="small" variant="outlined" color="error"
          onClick={() => cancelRequest(s.id)}
          sx={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
          Cancel
        </Button>
      </Stack>
    );
    if (req.blocked) {
      return <Chip label="Supplier blocked from orders" size="small" color="error" sx={{ fontSize: 11, fontWeight: 600 }} />;
    }
    // APPROVED + not blocked → show "Added" confirmation tag
    return (
      <Chip
        label="Added"
        size="small"
        color="success"
        sx={{ fontSize: 11, fontWeight: 700 }}
      />
    );
  }

  // Sort All Suppliers by id DESC (newest first) — statusFilter is now applied server-side.
  const sortedAllRows = useMemo(() => [...allRows].sort((a, b) => b.id - a.id), [allRows]);
  const filteredAllRows = sortedAllRows;

  // `nearRows` is already filtered server-side (isNew or near) and by search —
  // these just sort, and for Near Me, split into area-first / city-only sections.
  const newSuppliersRows = useMemo(() => [...nearRows].sort((a, b) => b.id - a.id), [nearRows]);

  const nearAreaMatches = useMemo(() =>
    nearRows.filter(s => !!userArea && s.area?.toLowerCase() === userArea.toLowerCase())
      .sort((a, b) => b.id - a.id),
    [nearRows, userArea]);

  const nearCityMatches = useMemo(() =>
    nearRows.filter(s =>
      !!userCity &&
      s.city?.toLowerCase() === userCity.toLowerCase() &&
      !(!!userArea && s.area?.toLowerCase() === userArea.toLowerCase())
    ).sort((a, b) => b.id - a.id),
    [nearRows, userArea, userCity]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h3" sx={{ color: brand.navy, fontWeight: 700, flex: 1 }}>Suppliers</Typography>
        <SearchWithFieldSelect
          field={searchField}
          onFieldChange={setSearchField}
          value={search}
          onChangeText={setSearch}
          onSearch={() => { handleSearch(); setAllPage(0); }}
        />
        <Tooltip title="Refresh"><span>
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => { void fetchAll(); setAllPage(0); }} disabled={loading || allLoading} sx={{ color: brand.navy }}>Refresh</Button>
        </span></Tooltip>
      </Stack>

      <Tabs value={tab} onChange={(_, v: number) => { setTab(v); setStatusFilter('all'); setAllPage(0); }}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label={`All Suppliers (${allTotal})`} sx={{ fontSize: 12 }} />
        <Tab label="New Suppliers" sx={{ fontSize: 12 }} />
        <Tab label="Near Me" sx={{ fontSize: 12 }} />
      </Tabs>

      {tab === 0 ? (
        <PeerTable
          rows={filteredAllRows}
          totalCount={allTotal}
          emptyMsg="No suppliers found."
          label="suppliers"
          renderAction={supplierAction}
          search={appliedSearch} onSearch={() => {}}
          hideSearchBox
          page={allPage} rowsPerPage={allRowsPerPage}
          onPageChange={setAllPage} onRowsPerPageChange={v => { setAllRowsPerPage(v); setAllPage(0); }}
          loading={allLoading}
          filterSlot={
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status"
                onChange={e => { setStatusFilter(e.target.value as typeof statusFilter); setAllPage(0); }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="not_sent">Not Sent</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          }
        />
      ) : tab === 1 ? (
        /* ─── New Suppliers tab — server-filtered, within 30 days ─── */
        <>
          {nearLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 680 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, width: 48 }}>#</TableCell>
                    <TableCell sx={thSx}>Name</TableCell>
                    <TableCell sx={thSx}>Location</TableCell>
                    <TableCell sx={thSx}>GSTIN / DL</TableCell>
                    <TableCell sx={thSx}>Phone / Email</TableCell>
                    <TableCell sx={thSx}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {newSuppliersRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: brand.inkSoft }}>
                        No suppliers registered in the last 30 days.
                      </TableCell>
                    </TableRow>
                  ) : newSuppliersRows.map((p, idx) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontSize: 12, color: brand.inkSoft, width: 48 }}>{idx + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.navy }}>{p.name}</Typography>
                          <TenantTypeChip type={p.tenantType} />
                          <Chip label="NEW" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 800, bgcolor: '#3DD68C', color: '#0E3520', '& .MuiChip-label': { px: 0.5 } }} />
                        </Stack>
                        {p.address && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>📍 {p.address}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>
                          {[p.area, p.city, p.district, p.state].filter(Boolean).join(', ') ? `📍 ${[p.area, p.city, p.district, p.state].filter(Boolean).join(', ')}` : '—'}
                        </Typography>
                        {p.pincode && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.pincode}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.2}>
                          {p.gstin && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>GST: {p.gstin}</Typography>}
                          {p.drugLicenseNumber && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>DL: {p.drugLicenseNumber}</Typography>}
                          {!p.gstin && !p.drugLicenseNumber && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>—</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          {p.phone && <Typography sx={{ fontSize: 12, color: brand.navy }}>📞 {p.phone}</Typography>}
                          {p.email && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.email}</Typography>}
                          {!p.phone && !p.email && <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>—</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell>{supplierAction(p)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        /* ─── Near Me tab ─── */
        <>
          {!userCity && !userArea && !nearLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>Update your area and city in your Profile to see nearby suppliers.</Alert>
          )}
          {nearLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 680 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, width: 48 }}>#</TableCell>
                    <TableCell sx={thSx}>Name</TableCell>
                    <TableCell sx={thSx}>Location</TableCell>
                    <TableCell sx={thSx}>GSTIN / DL</TableCell>
                    <TableCell sx={thSx}>Phone / Email</TableCell>
                    <TableCell sx={thSx}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nearAreaMatches.length === 0 && nearCityMatches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: brand.inkSoft }}>
                        {!userCity && !userArea
                          ? 'Update your area and city in Profile.'
                          : userArea
                            ? `No suppliers found in ${userArea} or ${userCity}.`
                            : `No suppliers found in ${userCity}.`}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {nearAreaMatches.length > 0 && (
                        <>
                          <TableRow sx={{ bgcolor: '#f3f4f8' }}>
                            <TableCell colSpan={6} sx={{ py: 0.75, px: 2, fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              In Your Area — {userArea}
                            </TableCell>
                          </TableRow>
                          {nearAreaMatches.map((p, idx) => (
                            <TableRow key={p.id} hover>
                              <TableCell sx={{ fontSize: 12, color: brand.inkSoft, width: 48 }}>{idx + 1}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: p.address ? 0.25 : 0 }}>
                                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.navy }}>{p.name}</Typography>
                                  <TenantTypeChip type={p.tenantType} />
                                </Stack>
                                {p.address && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>📍 {p.address}</Typography>}
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>
                                  {[p.area, p.city, p.district, p.state].filter(Boolean).join(', ') ? `📍 ${[p.area, p.city, p.district, p.state].filter(Boolean).join(', ')}` : '—'}
                                </Typography>
                                {p.pincode && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.pincode}</Typography>}
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.2}>
                                  {p.gstin && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>GST: {p.gstin}</Typography>}
                                  {p.drugLicenseNumber && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>DL: {p.drugLicenseNumber}</Typography>}
                                  {!p.gstin && !p.drugLicenseNumber && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>—</Typography>}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.25}>
                                  {p.phone && <Typography sx={{ fontSize: 12, color: brand.navy }}>📞 {p.phone}</Typography>}
                                  {p.email && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.email}</Typography>}
                                  {!p.phone && !p.email && <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>—</Typography>}
                                </Stack>
                              </TableCell>
                              <TableCell>{supplierAction(p)}</TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}
                      {nearCityMatches.length > 0 && (
                        <>
                          <TableRow sx={{ bgcolor: '#f3f4f8' }}>
                            <TableCell colSpan={6} sx={{ py: 0.75, px: 2, fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              In {userCity}
                            </TableCell>
                          </TableRow>
                          {nearCityMatches.map((p, idx) => (
                            <TableRow key={p.id} hover>
                              <TableCell sx={{ fontSize: 12, color: brand.inkSoft, width: 48 }}>{idx + 1}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: p.address ? 0.25 : 0 }}>
                                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.navy }}>{p.name}</Typography>
                                  <TenantTypeChip type={p.tenantType} />
                                </Stack>
                                {p.address && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>📍 {p.address}</Typography>}
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>
                                  {[p.area, p.city, p.district, p.state].filter(Boolean).join(', ') ? `📍 ${[p.area, p.city, p.district, p.state].filter(Boolean).join(', ')}` : '—'}
                                </Typography>
                                {p.pincode && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.pincode}</Typography>}
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.2}>
                                  {p.gstin && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>GST: {p.gstin}</Typography>}
                                  {p.drugLicenseNumber && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>DL: {p.drugLicenseNumber}</Typography>}
                                  {!p.gstin && !p.drugLicenseNumber && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>—</Typography>}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.25}>
                                  {p.phone && <Typography sx={{ fontSize: 12, color: brand.navy }}>📞 {p.phone}</Typography>}
                                  {p.email && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{p.email}</Typography>}
                                  {!p.phone && !p.email && <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>—</Typography>}
                                </Stack>
                              </TableCell>
                              <TableCell>{supplierAction(p)}</TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Snackbar open={Boolean(snack)} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─── RepRetailersView — Retailers only (/rep-retailers) ───────────────────

function RepRetailersView({ myTenantId }: { myTenantId: number }) {
  const { loading, fetchAll, myRetailers, autoRetailerIds, toggleRetailer, snack, setSnack } = useRepData(myTenantId);
  const navigate = useNavigate();
  const [tab, setTab] = useState(0); // 0=All Retailers, 1=My Retailers

  // ── Server search state (All Retailers tab) — independent of My Retailers' ──
  const [allSearch, setAllSearch] = useState('');
  const [allSearchField, setAllSearchField] = useState<SearchField>('name');
  const [appliedAllSearch, setAppliedAllSearch] = useState('');
  const [appliedAllSearchField, setAppliedAllSearchField] = useState<SearchField>('name');
  const [allPage, setAllPage] = useState(0);
  const [allRowsPerPage, setAllRowsPerPage] = useState(25);
  const [allRows, setAllRows] = useState<PeerTenant[]>([]);
  const [allTotal, setAllTotal] = useState(0);
  const [allLoading, setAllLoading] = useState(true);
  const handleAllSearch = () => {
    setAppliedAllSearch(allSearch.trim());
    setAppliedAllSearchField(allSearchField);
    setAllPage(0);
  };

  useEffect(() => {
    if (tab !== 0) return;
    let cancelled = false;
    setAllLoading(true);
    tenantApi.listPeers(appliedAllSearch || undefined, allPage, allRowsPerPage, 'RETAILER', { searchField: appliedAllSearchField })
      .then(res => { if (!cancelled) { setAllRows(res.content); setAllTotal(res.totalElements); setAllLoading(false); } })
      .catch(() => { if (!cancelled) setAllLoading(false); });
    return () => { cancelled = true; };
  }, [tab, appliedAllSearch, appliedAllSearchField, allPage, allRowsPerPage]);

  // ── My Retailers tab — client-side search, independent of All Retailers' ───
  const [mySearch, setMySearch] = useState('');
  const [mySearchField, setMySearchField] = useState<SearchField>('name');
  const [appliedMySearch, setAppliedMySearch] = useState('');
  const [appliedMySearchField, setAppliedMySearchField] = useState<SearchField>('name');
  const [myPage, setMyPage] = useState(0);
  const [myRowsPerPage, setMyRowsPerPage] = useState(25);
  const handleMySearch = () => {
    setAppliedMySearch(mySearch.trim());
    setAppliedMySearchField(mySearchField);
    setMyPage(0);
  };

  /** All Retailers tab is view-only — no Add / Remove buttons. */
  function allRetailersAction(_r: PeerTenant) {
    return null;
  }

  /** Action shown in My Retailers tab — Book Order + Remove.
   *  Auto-assigned retailers show a chip instead of the Remove button. */
  function myRetailersAction(r: PeerTenant) {
    const isAuto = autoRetailerIds.has(r.id);
    return (
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
        {isAuto && (
          <Chip
            label="Auto-Assigned"
            size="small"
            sx={{ fontSize: 10, height: 20, bgcolor: '#e8f4fd', color: '#1565c0', fontWeight: 600 }}
          />
        )}
        <Button
          size="small"
          variant="contained"
          onClick={() => navigate('/orders', {
            state: {
              bookOrderTab: 1,
              selectedRetailerId: r.id,
              selectedRetailerName: r.name,
              ts: Date.now(),
            },
          })}
          sx={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#7B61FF', '&:hover': { bgcolor: '#6a50ee' } }}
        >
          Book Order
        </Button>
        {!isAuto && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => toggleRetailer(r)}
            sx={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Remove
          </Button>
        )}
      </Box>
    );
  }


  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h3" sx={{ color: brand.navy, fontWeight: 700, flex: 1 }}>Retailers</Typography>
        {tab === 0 ? (
          <SearchWithFieldSelect
            field={allSearchField}
            onFieldChange={setAllSearchField}
            value={allSearch}
            onChangeText={setAllSearch}
            onSearch={handleAllSearch}
          />
        ) : (
          <SearchWithFieldSelect
            field={mySearchField}
            onFieldChange={setMySearchField}
            value={mySearch}
            onChangeText={setMySearch}
            onSearch={handleMySearch}
          />
        )}
        <Tooltip title="Refresh"><span>
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => { void fetchAll(); setAllPage(0); }} disabled={loading || allLoading} sx={{ color: brand.navy }}>Refresh</Button>
        </span></Tooltip>
      </Stack>

      <Tabs value={tab} onChange={(_, v: number) => { setTab(v); setMyPage(0); }}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label={`All Retailers (${allTotal})`} sx={{ fontSize: 12 }} />
        <Tab label={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>My Retailers</span>
            {myRetailers.length > 0 && <Chip label={myRetailers.length} size="small" color="success" sx={{ height: 18, fontSize: 10 }} />}
          </Stack>
        } sx={{ fontSize: 12 }} />
      </Tabs>

      {tab === 0 ? (
        <PeerTable
          rows={allRows}
          totalCount={allTotal}
          emptyMsg="No retailers found."
          label="retailers"
          renderAction={allRetailersAction}
          hideAction
          hideSearchBox
          search={appliedAllSearch} onSearch={() => {}}
          page={allPage} rowsPerPage={allRowsPerPage}
          onPageChange={setAllPage} onRowsPerPageChange={v => { setAllRowsPerPage(v); setAllPage(0); }}
          loading={allLoading}
        />
      ) : (
        <PeerTable
          rows={myRetailers}
          emptyMsg='No retailers in My Retailers yet. Retailers assigned by your suppliers appear here automatically.'
          label="retailers"
          renderAction={myRetailersAction}
          hideSearchBox
          search={appliedMySearch} searchField={appliedMySearchField} onSearch={() => {}}
          page={myPage} rowsPerPage={myRowsPerPage}
          onPageChange={setMyPage} onRowsPerPageChange={v => { setMyRowsPerPage(v); setMyPage(0); }}
          loading={loading}
        />
      )}

      <Snackbar open={Boolean(snack)} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─── Rep Detail / Edit Dialog ──────────────────────────────────────────────

function RepDetailDialog({
  open, rep, supplierId, retailers, repsList, repRequestId,
  initialMode, initialRetailerIds, onScopeChange, onClose,
}: {
  open: boolean;
  rep: PeerTenant | null;
  supplierId: number;
  retailers: PeerTenant[];
  repsList: PeerTenant[];
  /** ID of the RepRequest row (needed for PUT /rep-requests/{id}/retailer-scope). */
  repRequestId?: number;
  /** Initial mode loaded from backend. If absent, falls back to localStorage. */
  initialMode?: 'ALL' | 'LIMITED';
  /** Initial retailer IDs loaded from backend. */
  initialRetailerIds?: number[];
  /** Called after a successful backend scope save. */
  onScopeChange?: (mode: 'ALL' | 'LIMITED', retailerIds: number[]) => void;
  onClose: () => void;
}) {
  const [assignments, setAssignments] = useState<RepAssignment[]>(loadAssignments);
  const [retailerSearch, setRetailerSearch] = useState('');
  const [retailerFilter, setRetailerFilter] = useState<'all' | 'assigned' | 'not_assigned'>('all');
  const [page, setPage] = useState(0);
  const [snack, setSnack] = useState('');
  const rowsPerPage = 25;

  // Build: retailerId → rep name (from ALL assignments for this supplier)
  const retailerRepMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of assignments.filter(a => a.supplierTenantId === supplierId)) {
      for (const rid of a.retailerIds) {
        const repTenant = repsList.find(r => r.id === a.repTenantId);
        if (repTenant) map.set(rid, repTenant.name);
      }
    }
    return map;
  }, [assignments, supplierId, repsList]);

  const myAssignment = useMemo(
    () => rep ? assignments.find(a => a.supplierTenantId === supplierId && a.repTenantId === rep.id) : undefined,
    [assignments, supplierId, rep],
  );

  // Prefer backend-supplied initial values (from the RepRequestDto) over localStorage.
  // This ensures the supplier sees the persisted scope, not stale localStorage data.
  const mode: 'ALL' | 'LIMITED' = initialMode ?? myAssignment?.mode ?? 'ALL';
  const assignedRetailerIds: number[] = initialRetailerIds ?? myAssignment?.retailerIds ?? [];

  // uiMode is the source-of-truth for which button is visually active.
  // It is set explicitly on button clicks, NOT derived from data, so clicking
  // "Specific Retailers" always works even when IDs happen to match the mapped set.
  const [uiMode, setUiMode] = useState<'ALL' | 'MAPPED' | 'SPECIFIC'>(() => {
    if (mode === 'ALL') return 'ALL';
    const mappedIds = retailers.filter(r => r.linkedTenantParty != null).map(r => r.id);
    if (
      mappedIds.length > 0 &&
      assignedRetailerIds.length === mappedIds.length &&
      mappedIds.every(id => assignedRetailerIds.includes(id))
    ) return 'MAPPED';
    return 'SPECIFIC';
  });

  const filteredRetailers = useMemo(() => {
    let list = retailers;
    if (retailerFilter === 'assigned') list = list.filter(r => assignedRetailerIds.includes(r.id));
    else if (retailerFilter === 'not_assigned') list = list.filter(r => !retailerRepMap.has(r.id));
    if (retailerSearch.trim()) {
      const q = retailerSearch.toLowerCase();
      list = list.filter(r =>
        r.name.toLowerCase().includes(q) ||
        (r.gstin ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [retailers, retailerFilter, retailerSearch, retailerRepMap, assignedRetailerIds]);

  const paged = filteredRetailers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // All hooks called — safe to early-return
  if (!rep) return null;

  async function setMode(newMode: 'ALL' | 'LIMITED', nextUiMode: 'ALL' | 'SPECIFIC' = newMode === 'ALL' ? 'ALL' : 'SPECIFIC') {
    setUiMode(nextUiMode);
    const updated = [
      ...assignments.filter(a => !(a.supplierTenantId === supplierId && a.repTenantId === rep!.id)),
      { supplierTenantId: supplierId, repTenantId: rep!.id, mode: newMode, retailerIds: assignedRetailerIds },
    ];
    setAssignments(updated);
    saveAssignments(updated);
    if (repRequestId) {
      try {
        await updateRetailerScope(repRequestId, newMode, newMode === 'ALL' ? [] : assignedRetailerIds);
        onScopeChange?.(newMode, newMode === 'ALL' ? [] : assignedRetailerIds);
      } catch {
        setSnack('Failed to save scope to server — changes saved locally only.');
      }
    }
  }

  // One-click: assign all retailers that are already mapped (have a linked TenantParty)
  const mappedRetailers = retailers.filter(r => r.linkedTenantParty != null);

  async function assignMappedRetailers() {
    const mappedIds = mappedRetailers.map(r => r.id);
    if (mappedIds.length === 0) { setSnack('No mapped retailers found.'); return; }
    setUiMode('MAPPED');
    const updated = [
      ...assignments.filter(a => !(a.supplierTenantId === supplierId && a.repTenantId === rep!.id)),
      { supplierTenantId: supplierId, repTenantId: rep!.id, mode: 'LIMITED' as const, retailerIds: mappedIds },
    ];
    setAssignments(updated);
    saveAssignments(updated);
    if (repRequestId) {
      try {
        await updateRetailerScope(repRequestId, 'LIMITED', mappedIds);
        onScopeChange?.('LIMITED', mappedIds);
        setSnack(`${mappedIds.length} mapped retailer${mappedIds.length !== 1 ? 's' : ''} assigned.`);
      } catch {
        setSnack('Saved locally — server sync failed.');
      }
    } else {
      setSnack(`${mappedIds.length} mapped retailer${mappedIds.length !== 1 ? 's' : ''} assigned.`);
    }
  }

  async function toggleRetailer(retailerId: number) {
    const isAssigned = assignedRetailerIds.includes(retailerId);
    const newIds = isAssigned
      ? assignedRetailerIds.filter(id => id !== retailerId)
      : [...assignedRetailerIds, retailerId];
    const updated = [
      ...assignments.filter(a => !(a.supplierTenantId === supplierId && a.repTenantId === rep!.id)),
      { supplierTenantId: supplierId, repTenantId: rep!.id, mode: 'LIMITED' as const, retailerIds: newIds },
    ];
    setAssignments(updated);
    saveAssignments(updated);
    setSnack(isAssigned ? 'Retailer removed' : 'Retailer added');
    if (repRequestId) {
      try {
        await updateRetailerScope(repRequestId, 'LIMITED', newIds);
        onScopeChange?.('LIMITED', newIds);
      } catch {
        setSnack('Failed to save scope to server — changes saved locally only.');
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ zIndex: (theme) => theme.zIndex.modal, top: { xs: 51, md: 58 } }}
      BackdropProps={{ sx: { top: { xs: 51, md: 58 } } }}
      PaperProps={{
        sx: {
          m: 0,
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          maxHeight: '100%',
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Full-screen app bar ── */}
      <AppBar sx={{ position: 'relative', bgcolor: brand.navy, boxShadow: 'none', flexShrink: 0 }}>
        <Toolbar>
          <Tooltip title="Close">
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close" sx={{ mr: 1 }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#fff', lineHeight: 1.2 }}>
              {rep.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', mt: 0.25 }}>
              Sales Representative
            </Typography>
          </Box>
          <Chip
            label={
              uiMode === 'ALL'
                ? 'Access: All Retailers'
                : uiMode === 'MAPPED'
                  ? `Access: ${mappedRetailers.length} Mapped Retailers`
                  : `Access: ${assignedRetailerIds.length} Specific Retailer${assignedRetailerIds.length !== 1 ? 's' : ''}`
            }
            size="small"
            sx={{
              bgcolor: uiMode === 'MAPPED' ? 'rgba(5,150,105,0.3)' : 'rgba(255,255,255,0.18)',
              color: '#fff', fontWeight: 600, fontSize: 11,
            }}
          />
        </Toolbar>
      </AppBar>

      {/* ── Scrollable content ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', bgcolor: '#f8f9fc' }}>
        <Container maxWidth="lg" sx={{ py: 1.5 }}>

          {/* Rep contact info — compact horizontal strip */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2.5, py: 1.25, mb: 1.5 }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" rowGap={1} alignItems="flex-start">
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: brand.inkSoft, textTransform: 'uppercase', letterSpacing: '0.07em', alignSelf: 'center', whiteSpace: 'nowrap' }}>
                Contact
              </Typography>
              {[
                { label: 'Phone', value: rep.phone || '—' },
                { label: 'Email', value: rep.email || '—' },
                { label: 'Location', value: [rep.city, rep.district, rep.state].filter(Boolean).join(', ') || '—' },
                { label: 'Pincode', value: rep.pincode || '—' },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: 9, color: brand.inkSoft, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: brand.navy, fontWeight: value === '—' ? 400 : 500 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Retailer access */}
          <Box sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.25 }}>
              <Typography sx={{ fontWeight: 700, color: brand.navy, fontSize: 14, flex: 1 }}>
                Retailer Access
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.75}>
                {/* ── All Retailers ── */}
                <Button
                  size="small"
                  variant={uiMode === 'ALL' ? 'contained' : 'outlined'}
                  onClick={() => setMode('ALL', 'ALL')}
                  sx={{
                    fontSize: 11, fontWeight: 700,
                    ...(uiMode === 'ALL'
                      ? { bgcolor: brand.navy, borderColor: brand.navy, '&:hover': { bgcolor: '#1e3460' } }
                      : {}),
                  }}
                >
                  {uiMode === 'ALL' ? '✓ ' : ''}All Retailers
                </Button>

                {/* ── Assign All Mapped Retailers ── */}
                {mappedRetailers.length > 0 && (
                  <Tooltip title="Assign all retailers that are already mapped (linked) in your Parties screen">
                    <Button
                      size="small"
                      variant={uiMode === 'MAPPED' ? 'contained' : 'outlined'}
                      onClick={assignMappedRetailers}
                      sx={{
                        fontSize: 11, fontWeight: 700,
                        ...(uiMode === 'MAPPED'
                          ? { bgcolor: '#059669', borderColor: '#059669', color: '#fff', '&:hover': { bgcolor: '#047857' } }
                          : { borderColor: '#059669', color: '#059669', '&:hover': { bgcolor: '#ecfdf5', borderColor: '#047857' } }),
                      }}
                    >
                      {uiMode === 'MAPPED' ? '✓ ' : ''}Assign All Mapped Retailers ({mappedRetailers.length})
                    </Button>
                  </Tooltip>
                )}

                {/* ── Specific Retailers ── */}
                <Button
                  size="small"
                  variant={uiMode === 'SPECIFIC' ? 'contained' : 'outlined'}
                  onClick={() => setMode('LIMITED', 'SPECIFIC')}
                  sx={{
                    fontSize: 11, fontWeight: 700,
                    ...(uiMode === 'SPECIFIC'
                      ? { bgcolor: brand.violet, borderColor: brand.violet, '&:hover': { bgcolor: brand.violetSoft } }
                      : {}),
                  }}
                >
                  {uiMode === 'SPECIFIC' ? '✓ ' : ''}Specific Retailers
                  {uiMode === 'SPECIFIC' && assignedRetailerIds.length > 0 && ` (${assignedRetailerIds.length})`}
                </Button>
              </Stack>
            </Stack>

            {/* ── Active mode description ── */}
            {uiMode === 'ALL' && (
              <Alert severity="success" sx={{ fontSize: 12, py: 0.5 }}>
                <strong>All Retailers</strong> — This rep can access all your retailers while placing orders.
              </Alert>
            )}

            {/* MAPPED and SPECIFIC both show a retailer table; MAPPED is read-only */}
            {(uiMode === 'MAPPED' || uiMode === 'SPECIFIC') && (
              <>
                {uiMode === 'MAPPED' && (
                  <Alert severity="success" sx={{ mb: 1.25, fontSize: 12, py: 0.5 }}>
                    <strong>Assigned All Mapped Retailers</strong> — {mappedRetailers.length} retailer{mappedRetailers.length !== 1 ? 's' : ''} linked via your Parties screen are accessible to this rep. Actions are disabled in this view.
                  </Alert>
                )}

                {uiMode === 'SPECIFIC' && (
                  <>
                    <Alert severity="info" sx={{ mb: 1.25, fontSize: 12, py: 0.5 }}>
                      <strong>Specific Retailers ({assignedRetailerIds.length} selected)</strong> — Only the retailers you tick below are accessible to this rep.
                    </Alert>

                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Search by name or GST…"
                        value={retailerSearch}
                        onChange={e => { setRetailerSearch(e.target.value); setPage(0); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        sx={{ width: 280 }}
                      />
                      <ToggleButtonGroup
                        size="small" exclusive
                        value={retailerFilter}
                        onChange={(_, v) => { if (v) { setRetailerFilter(v as typeof retailerFilter); setPage(0); } }}
                      >
                        <ToggleButton value="all" sx={{ fontSize: 11, py: 0.35, px: 1.5 }}>All</ToggleButton>
                        <ToggleButton value="assigned" sx={{ fontSize: 11, py: 0.35, px: 1.5 }}>Assigned</ToggleButton>
                        <ToggleButton value="not_assigned" sx={{ fontSize: 11, py: 0.35, px: 1.5 }}>Not Assigned</ToggleButton>
                      </ToggleButtonGroup>
                      {retailerFilter === 'assigned' && (
                        <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>
                          {filteredRetailers.length} assigned to this rep
                        </Typography>
                      )}
                    </Stack>
                  </>
                )}

                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 700 }}>
                    <TableHead>
                      <TableRow>
                        {['#', 'Name', 'Location', 'GSTIN / DL', 'Assigned Rep', 'Action'].map(h => (
                          <TableCell key={h} sx={{ ...thSx, py: 0.6 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(uiMode === 'MAPPED' ? mappedRetailers : paged).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: brand.inkSoft }}>
                            No retailers found.
                          </TableCell>
                        </TableRow>
                      ) : (uiMode === 'MAPPED' ? mappedRetailers : paged).map((r, idx) => {
                        const assignedRep = retailerRepMap.get(r.id);
                        const isThisRep = assignedRetailerIds.includes(r.id);
                        const locationParts = [r.city, r.district, r.state].filter(Boolean).join(', ');
                        return (
                          <TableRow key={r.id} hover sx={{ bgcolor: uiMode === 'MAPPED' ? '#f0fff4' : isThisRep ? '#f0f7ff' : undefined }}>
                            <TableCell sx={{ fontSize: 11, color: brand.inkSoft, width: 40, py: 0.5 }}>
                              {(uiMode === 'SPECIFIC' ? page * rowsPerPage : 0) + idx + 1}
                            </TableCell>
                            {/* Name + type chip */}
                            <TableCell sx={{ py: 0.5 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: brand.navy, lineHeight: 1.3 }}>
                                  {r.name}
                                </Typography>
                                <TenantTypeChip type={r.tenantType} />
                              </Stack>
                              {r.address && (
                                <Typography sx={{ fontSize: 10, color: brand.inkSoft, lineHeight: 1.2 }}>📍 {r.address}</Typography>
                              )}
                              {r.email && (
                                <Typography sx={{ fontSize: 10, color: brand.inkSoft, lineHeight: 1.2 }}>{r.email}</Typography>
                              )}
                            </TableCell>
                            {/* Location */}
                            <TableCell sx={{ py: 0.5 }}>
                              <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                                {locationParts ? `📍 ${locationParts}` : '—'}
                              </Typography>
                              {r.pincode && (
                                <Typography sx={{ fontSize: 10, color: brand.inkSoft }}>PIN: {r.pincode}</Typography>
                              )}
                            </TableCell>
                            {/* GSTIN + DL No */}
                            <TableCell sx={{ py: 0.5 }}>
                              <Stack spacing={0.2}>
                                {r.gstin ? (
                                  <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                                    GST: {r.gstin}
                                  </Typography>
                                ) : null}
                                {r.drugLicenseNumber ? (
                                  <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                                    DL: {r.drugLicenseNumber}
                                  </Typography>
                                ) : null}
                                {!r.gstin && !r.drugLicenseNumber && (
                                  <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>—</Typography>
                                )}
                              </Stack>
                            </TableCell>
                            {/* Assigned rep */}
                            <TableCell sx={{ py: 0.5 }}>
                              {assignedRep
                                ? <Chip label={assignedRep} size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#e8f4fd', color: brand.navy }} />
                                : <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>—</Typography>}
                            </TableCell>
                            {/* Action — disabled read-only chip in MAPPED mode */}
                            <TableCell sx={{ py: 0.5 }}>
                              {uiMode === 'MAPPED' ? (
                                <Chip
                                  label="Mapped"
                                  size="small"
                                  sx={{ fontSize: 10, height: 20, bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, borderRadius: 0.5 }}
                                />
                              ) : (
                                <Button
                                  size="small"
                                  variant={isThisRep ? 'outlined' : 'contained'}
                                  color={isThisRep ? 'error' : 'primary'}
                                  onClick={() => toggleRetailer(r.id)}
                                  sx={{
                                    fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', py: 0.25,
                                    ...(!isThisRep && { bgcolor: brand.navy, '&:hover': { bgcolor: '#1e3460' } }),
                                  }}
                                >
                                  {isThisRep ? 'Remove' : 'Add'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {uiMode === 'SPECIFIC' && (
                  <PaginationFooter
                    page={page}
                    totalPages={Math.max(1, Math.ceil(filteredRetailers.length / rowsPerPage))}
                    total={filteredRetailers.length}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onPageChange={setPage}
                    onRowsPerPageChange={() => {}}
                    label="retailers"
                  />
                )}
              </>
            )}
          </Box>
        </Container>
      </Box>

      <Snackbar open={Boolean(snack)} autoHideDuration={2000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Dialog>
  );
}

// ─── Block / Unblock Confirmation Dialog ──────────────────────────────────

function BlockDialog({
  open, rep, isBlocked, onClose, onConfirm,
}: {
  open: boolean;
  rep: PeerTenant | null;
  isBlocked: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!rep) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: isBlocked ? 'success.main' : 'error.main' }}>
        {isBlocked ? 'Unblock Representative' : 'Block Representative'}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14 }}>
          {isBlocked
            ? `Allow ${rep.name} to access your products again while placing orders?`
            : `Are you sure you want to block ${rep.name}? Blocked reps cannot access your product catalogue while placing orders.`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color={isBlocked ? 'success' : 'error'}>
          {isBlocked ? 'Unblock' : 'Block'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── SupplierView (WHOLESALER / DISTRIBUTOR) ───────────────────────────────

function SupplierView({ myTenantId, myName: _myName }: { myTenantId: number; myName: string }) {
  const [retailers, setRetailers] = useState<PeerTenant[]>([]);
  /** Small rep cache used by RepDetailDialog for name lookups. Loaded once at startup. */
  const [repsCache, setRepsCache] = useState<PeerTenant[]>([]);
  const [baseLoading, setBaseLoading] = useState(true); // retailers + incomingRequests
  const [incomingRequests, setIncomingRequests] = useState<RepRequestDto[]>([]);
  const [blocked, setBlocked] = useState<BlockedEntry[]>(loadBlocked);
  const [tab, setTab] = useState(0); // 0=All Reps, 1=My Reps, 2=Pending
  const [detailRep, setDetailRep] = useState<PeerTenant | null>(null);
  const [blockRep, setBlockRep] = useState<PeerTenant | null>(null);
  const [snack, setSnack] = useState('');

  // ── Server search state (All Reps tab) ──────────────────────────────────
  const [allSearch, setAllSearch] = useState('');
  const [allPage, setAllPage] = useState(0);
  const [allRowsPerPage, setAllRowsPerPage] = useState(25);
  const [allRows, setAllRows] = useState<PeerTenant[]>([]);
  const [allTotal, setAllTotal] = useState(0);
  const [allLoading, setAllLoading] = useState(true);
  const [repFilter, setRepFilter] = useState<'all' | 'my_reps' | 'pending' | 'blocked' | 'not_added' | 'not_blocked'>('all');
  const debouncedAllSearch = useDebounce(allSearch, 400);

  // Server search effect: fetch from DB whenever search/page changes (All Reps tab)
  useEffect(() => {
    if (tab !== 0) return;
    let cancelled = false;
    setAllLoading(true);
    tenantApi.listReps(debouncedAllSearch || undefined, allPage, allRowsPerPage)
      .then(res => {
        if (!cancelled) {
          // Merge with any pending-request reps not yet in the directory
          setAllRows(res.content);
          setAllTotal(res.totalElements);
          setAllLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setAllLoading(false); });
    return () => { cancelled = true; };
  }, [tab, debouncedAllSearch, allPage, allRowsPerPage]);

  // ── Client-side state for My Reps / Pending tabs ─────────────────────────
  const [mySearch, setMySearch] = useState('');
  const [myPage, setMyPage] = useState(0);
  const [myRowsPerPage, setMyRowsPerPage] = useState(25);
  const [myFilter, setMyFilter] = useState<'all' | 'blocked' | 'not_blocked'>('all');

  async function fetchBase() {
    setBaseLoading(true);
    const [peersRes, repsRes, reqRes] = await Promise.allSettled([
      tenantApi.listPeers(undefined, 0, 200, 'RETAILER'),
      tenantApi.listReps(undefined, 0, 200),
      getIncomingRepRequests(),
    ]);
    if (peersRes.status === 'fulfilled') setRetailers(peersRes.value.content);
    if (repsRes.status === 'fulfilled') setRepsCache(repsRes.value.content);
    if (reqRes.status === 'fulfilled') setIncomingRequests(reqRes.value);
    setBaseLoading(false);
  }

  useEffect(() => { void fetchBase(); }, []);

  const pendingRequests = incomingRequests.filter(r => r.status === 'PENDING');

  const isMyRep = (repId: number) =>
    incomingRequests.some(r => r.repTenantId === repId && r.status === 'APPROVED');

  const isRepBlocked = (repId: number) =>
    blocked.some(b => b.supplierTenantId === myTenantId && b.repTenantId === repId);

  async function toggleMyRep(rep: PeerTenant) {
    try {
      const result = await directToggleRep(rep.id);
      setIncomingRequests(prev => [
        ...prev.filter(r => r.repTenantId !== rep.id),
        result,
      ]);
      setSnack(result.status === 'APPROVED'
        ? `${rep.name} added to My Reps`
        : `${rep.name} removed from My Reps`);
    } catch {
      setSnack('Action failed. Please try again.');
    }
  }

  // All Reps tab: apply repFilter as client overlay on server-fetched rows
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredAllRows = useMemo(() => {
    if (repFilter === 'all') return allRows;
    return allRows.filter(rep => {
      const isBlocked = isRepBlocked(rep.id);
      const isMine = isMyRep(rep.id);
      const hasPending = incomingRequests.some(r => r.repTenantId === rep.id && r.status === 'PENDING');
      if (repFilter === 'my_reps') return isMine;
      if (repFilter === 'pending') return hasPending;
      if (repFilter === 'blocked') return isBlocked;
      if (repFilter === 'not_added') return !isMine && !isBlocked && !hasPending;
      if (repFilter === 'not_blocked') return !isBlocked;
      return true;
    });
  }, [allRows, repFilter, blocked, incomingRequests]); // isRepBlocked/isMyRep intentionally omitted

  // My Reps tab — derived from incomingRequests, enriched from repsCache
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const myReps = useMemo(() => {
    const base = incomingRequests
      .filter(r => r.status === 'APPROVED')
      .map(req => {
        const found = repsCache.find(r => r.id === req.repTenantId);
        return found ?? ({
          id: req.repTenantId,
          name: req.repName ?? `Rep #${req.repTenantId}`,
          tenantType: 'REPRESENTATIVE',
          city: '', district: '', state: '', phone: '', email: '', address: '', gstin: '', pincode: '',
        } as unknown as PeerTenant);
      });
    if (myFilter === 'blocked') return base.filter(r => isRepBlocked(r.id));
    if (myFilter === 'not_blocked') return base.filter(r => !isRepBlocked(r.id));
    return base;
  }, [incomingRequests, repsCache, myFilter, blocked]); // isRepBlocked intentionally omitted

  // Pending tab — derived from pendingRequests, enriched from repsCache
  const pendingReps = useMemo(() => pendingRequests.map(req => {
    const found = repsCache.find(r => r.id === req.repTenantId);
    return found ?? ({
      id: req.repTenantId,
      name: req.repName ?? `Rep #${req.repTenantId}`,
      tenantType: 'REPRESENTATIVE',
      city: '', district: '', state: '', phone: '', email: '', address: '', gstin: '', pincode: '',
    } as unknown as PeerTenant);
  }), [pendingRequests, repsCache]);

  async function handleAccept(repId: number) {
    const req = incomingRequests.find(r => r.repTenantId === repId && r.status === 'PENDING');
    if (!req) return;
    try {
      const updated = await acceptRepRequest(req.id);
      setIncomingRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
      setSnack('Request approved — rep added to My Reps');
    } catch { setSnack('Failed to accept. Please try again.'); }
  }

  async function handleReject(repId: number) {
    const req = incomingRequests.find(r => r.repTenantId === repId && r.status === 'PENDING');
    if (!req) return;
    try {
      const updated = await rejectRepRequest(req.id);
      setIncomingRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
      setSnack('Request rejected');
    } catch { setSnack('Failed to reject. Please try again.'); }
  }

  async function handleToggleBlock() {
    if (!blockRep) return;
    const alreadyBlocked = isRepBlocked(blockRep.id);
    const updatedBlocked = alreadyBlocked
      ? blocked.filter(b => !(b.supplierTenantId === myTenantId && b.repTenantId === blockRep.id))
      : [...blocked, { supplierTenantId: myTenantId, repTenantId: blockRep.id }];
    setBlocked(updatedBlocked);
    saveBlocked(updatedBlocked);
    try {
      const updated = alreadyBlocked
        ? await unblockRepApi(blockRep.id)
        : await blockRepApi(blockRep.id);
      setIncomingRequests(prev => prev.map(r => r.repTenantId === blockRep.id ? updated : r));
    } catch { /* backend may not be up yet, UI already updated locally */ }
    setSnack(alreadyBlocked ? `${blockRep.name} unblocked` : `${blockRep.name} blocked`);
    setBlockRep(null);
  }

  // Shared row renderer (All Reps + My Reps + Pending)
  function renderRepRow(rep: PeerTenant, idx: number, startIdx: number, currentTab: number) {
    const blocked_ = isRepBlocked(rep.id);
    const myRep_ = isMyRep(rep.id);
    const locationParts = [rep.city, rep.district, rep.state].filter(Boolean);
    const pendingReq = incomingRequests.find(r => r.repTenantId === rep.id && r.status === 'PENDING');
    return (
      <TableRow key={rep.id} hover sx={{ bgcolor: blocked_ ? '#fff5f5' : myRep_ ? '#f0fdf4' : 'background.paper' }}>
        <TableCell sx={{ fontSize: 12, color: brand.inkSoft, width: 48 }}>{startIdx + idx + 1}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: rep.address ? 0.25 : 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.navy }}>{rep.name}</Typography>
            <TenantTypeChip type={rep.tenantType} />
          </Stack>
          {rep.address && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>📍 {rep.address}</Typography>}
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>{locationParts.length ? `📍 ${locationParts.join(', ')}` : '—'}</Typography>
          {rep.pincode && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{rep.pincode}</Typography>}
        </TableCell>
        <TableCell>
          <Stack spacing={0.25}>
            {rep.phone && <Typography sx={{ fontSize: 12, color: brand.navy }}>📞 {rep.phone}</Typography>}
            {rep.email && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{rep.email}</Typography>}
            {!rep.phone && !rep.email && <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>—</Typography>}
          </Stack>
        </TableCell>
        {currentTab === 2 ? (
          <>
            <TableCell sx={{ fontSize: 12, color: brand.inkSoft }}>
              {pendingReq ? new Date(pendingReq.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={0.5}>
                <Button size="small" variant="contained" color="success" startIcon={<AcceptIcon />}
                  onClick={() => handleAccept(rep.id)} sx={{ fontSize: 11, fontWeight: 600 }}>Accept</Button>
                <Button size="small" variant="outlined" color="error" startIcon={<RejectIcon />}
                  onClick={() => handleReject(rep.id)} sx={{ fontSize: 11, fontWeight: 600 }}>Reject</Button>
              </Stack>
            </TableCell>
          </>
        ) : (
          <>
            <TableCell>
              <Stack spacing={0.5} direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
                {blocked_ ? <Chip label="Blocked" size="small" color="error" sx={{ fontSize: 11 }} />
                  : myRep_ ? <Chip label="My Rep" size="small" color="success" sx={{ fontSize: 11, fontWeight: 700 }} />
                  : <Chip label="Not Added" size="small" sx={{ fontSize: 11, bgcolor: '#f1f5f9', color: brand.inkSoft }} />}
              </Stack>
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                <Button size="small" variant={myRep_ ? 'outlined' : 'contained'} color={myRep_ ? 'error' : 'primary'}
                  onClick={() => toggleMyRep(rep)}
                  sx={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', ...(!myRep_ && { bgcolor: brand.navy, '&:hover': { bgcolor: '#1e3460' } }) }}>
                  {myRep_ ? 'Remove from My Reps' : 'Add as My Rep'}
                </Button>
                <Button size="small" variant="outlined" startIcon={<EditIcon />}
                  onClick={() => setDetailRep(rep)} sx={{ fontSize: 11, fontWeight: 600 }}>View/Edit</Button>
                <Button size="small" variant="outlined" color={blocked_ ? 'success' : 'error'}
                  startIcon={blocked_ ? <UnblockIcon /> : <BlockIcon />}
                  onClick={() => setBlockRep(rep)} sx={{ fontSize: 11, fontWeight: 600 }}>
                  {blocked_ ? 'Unblock' : 'Block'}
                </Button>
              </Stack>
            </TableCell>
          </>
        )}
      </TableRow>
    );
  }

  function RepTable({ rows: tableRows, isServer, total, search: srch, onSearch: onSrch, page: pg, rowsPerPage: rpp,
    onPageChange, onRowsPerPageChange, loading: ld, emptyMsg, filterSlot: fs, currentTab, label: tableLabel = 'sales reps' }: {
    rows: PeerTenant[]; isServer?: boolean; total?: number; search: string; onSearch: (v: string) => void;
    page: number; rowsPerPage: number; onPageChange: (p: number) => void; onRowsPerPageChange: (r: number) => void;
    loading: boolean; emptyMsg: string; filterSlot?: React.ReactNode; currentTab: number; label?: string;
  }) {
    const q = srch.trim().toLowerCase();
    // client-side search (only for non-server mode)
    const processed = isServer ? tableRows : (q
      ? tableRows.filter(r =>
          r.name.toLowerCase().includes(q) ||
          (r.city ?? '').toLowerCase().includes(q) ||
          (r.gstin ?? '').toLowerCase().includes(q) ||
          (r.drugLicenseNumber ?? '').toLowerCase().includes(q) ||
          (r.phone ?? '').includes(q))
      : tableRows);
    const paginated = isServer ? processed : processed.slice(pg * rpp, pg * rpp + rpp);
    const count = isServer ? (total ?? 0) : processed.length;
    return (
      <>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap">
          <TextField size="small" placeholder="Search by name, city, GST, DL or phone…" value={srch}
            onChange={e => { onSrch(e.target.value); if (!isServer) onPageChange(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: { xs: '100%', sm: 340 } }}
          />
          {fs && <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>{fs}</Box>}
        </Stack>
        {ld ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
          <>
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, width: 48 }}>#</TableCell>
                    <TableCell sx={thSx}>Name</TableCell>
                    <TableCell sx={thSx}>Location</TableCell>
                    <TableCell sx={thSx}>Phone / Email</TableCell>
                    <TableCell sx={thSx}>{currentTab === 2 ? 'Requested' : 'Status'}</TableCell>
                    <TableCell sx={thSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: brand.inkSoft }}>{emptyMsg}</TableCell>
                    </TableRow>
                  ) : paginated.map((rep, idx) => renderRepRow(rep, idx, pg * rpp, currentTab))}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationFooter
              page={pg}
              totalPages={Math.max(1, Math.ceil(count / rpp))}
              total={count}
              rowsPerPage={rpp}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              label={tableLabel}
            />
          </>
        )}
      </>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
        <Typography variant="h3" sx={{ color: brand.navy, fontWeight: 700, flex: 1 }}>Sales Representatives</Typography>
        <Tooltip title="Refresh"><span>
          <Button size="small" startIcon={<RefreshIcon />}
            onClick={() => { void fetchBase(); setAllPage(0); }}
            disabled={baseLoading || allLoading} sx={{ color: brand.navy }}>Refresh</Button>
        </span></Tooltip>
      </Stack>

      <Tabs value={tab}
        onChange={(_, v: number) => { setTab(v); setRepFilter('all'); setMyFilter('all'); setAllPage(0); setMyPage(0); }}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label={`All Reps (${allTotal})`} />
        <Tab label={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>My Reps</span>
            {myReps.length > 0 && <Chip label={myReps.length} size="small" color="success" sx={{ height: 18, fontSize: 10 }} />}
          </Stack>
        } />
        <Tab label={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>Pending Requests</span>
            {pendingRequests.length > 0 && <Chip label={pendingRequests.length} size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />}
          </Stack>
        } />
      </Tabs>

      {tab === 0 && (
        <RepTable
          rows={filteredAllRows}
          isServer total={repFilter === 'all' ? allTotal : filteredAllRows.length}
          search={allSearch} onSearch={v => { setAllSearch(v); setAllPage(0); }}
          page={allPage} rowsPerPage={allRowsPerPage}
          onPageChange={setAllPage} onRowsPerPageChange={v => { setAllRowsPerPage(v); setAllPage(0); }}
          loading={allLoading} emptyMsg="No sales representatives found."
          currentTab={0}
          filterSlot={
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter</InputLabel>
              <Select value={repFilter} label="Filter"
                onChange={e => { setRepFilter(e.target.value as typeof repFilter); setAllPage(0); }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="my_reps">My Reps</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="not_added">Not Added</MenuItem>
              </Select>
            </FormControl>
          }
        />
      )}

      {tab === 1 && (
        <RepTable
          rows={myReps}
          search={mySearch} onSearch={v => { setMySearch(v); setMyPage(0); }}
          page={myPage} rowsPerPage={myRowsPerPage}
          onPageChange={setMyPage} onRowsPerPageChange={v => { setMyRowsPerPage(v); setMyPage(0); }}
          loading={baseLoading} emptyMsg='No reps added yet. Go to "All Reps" to add some.'
          currentTab={1}
          filterSlot={
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Blocked</InputLabel>
              <Select value={myFilter} label="Blocked"
                onChange={e => { setMyFilter(e.target.value as typeof myFilter); setMyPage(0); }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="not_blocked">Not Blocked</MenuItem>
              </Select>
            </FormControl>
          }
        />
      )}

      {tab === 2 && (
        <RepTable
          rows={pendingReps}
          search={mySearch} onSearch={v => { setMySearch(v); setMyPage(0); }}
          page={myPage} rowsPerPage={myRowsPerPage}
          onPageChange={setMyPage} onRowsPerPageChange={v => { setMyRowsPerPage(v); setMyPage(0); }}
          loading={baseLoading} emptyMsg="No pending requests."
          currentTab={2}
        />
      )}

      <RepDetailDialog
        open={Boolean(detailRep)}
        rep={detailRep}
        supplierId={myTenantId}
        retailers={retailers}
        repsList={repsCache}
        repRequestId={detailRep
          ? incomingRequests.find(r => r.repTenantId === detailRep.id && r.status === 'APPROVED')?.id
          : undefined}
        initialMode={detailRep
          ? (incomingRequests.find(r => r.repTenantId === detailRep.id && r.status === 'APPROVED')?.retailerMode ?? 'ALL')
          : undefined}
        initialRetailerIds={detailRep
          ? (incomingRequests.find(r => r.repTenantId === detailRep.id && r.status === 'APPROVED')?.retailerIds ?? [])
          : undefined}
        onScopeChange={(mode, ids) => {
          // Reflect the saved scope back into incomingRequests state for the session
          setIncomingRequests(prev => prev.map(r =>
            r.repTenantId === detailRep?.id && r.status === 'APPROVED'
              ? { ...r, retailerMode: mode, retailerIds: ids }
              : r,
          ));
        }}
        onClose={() => setDetailRep(null)}
      />

      <BlockDialog
        open={Boolean(blockRep)}
        rep={blockRep}
        isBlocked={blockRep ? isRepBlocked(blockRep.id) : false}
        onClose={() => setBlockRep(null)}
        onConfirm={handleToggleBlock}
      />

      <Snackbar open={Boolean(snack)} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─── Exports ───────────────────────────────────────────────────────────────

export function SalesRepsPage() {
  const { user } = useAuthStore();
  if (!user) return null;
  if (user.tenantType === 'REPRESENTATIVE') return <RepView myTenantId={user.tenantId} myName={user.tenantName} />;
  if (user.tenantType === 'WHOLESALER') return <SupplierView myTenantId={user.tenantId} myName={user.tenantName} />;
  return null; // RETAILER and others don't have this page
}

export function RepRetailersPage() {
  const { user } = useAuthStore();
  if (!user || user.tenantType !== 'REPRESENTATIVE') return null;
  return <RepRetailersView myTenantId={user.tenantId} />;
}
